import {graphql} from 'gatsby'
import Cookies from 'js-cookie'
import {m as motion} from 'motion/react'
import React from 'react'

import FeatureBlock from '@/components/feature-block'
import SimpleSignupForm from '@/components/simple-signup-form'
import {paramCookiesMap} from '@/config/cookies'
import {BackgroundWrapper, Section} from '@/styles/grid'
import {decodeBase64} from '@/utils/decode-base-64'
import getEnvironmentFormConfig from '@/utils/get-environment-field'
import {isValidBackgroundColor} from '@/utils/is-valid-background-color'
import {isValidTagColor} from '@/utils/is-valid-tag-color'
import replacePlaceholders from '@/utils/replace-placeholders'

import type {ReferralHeroSliceFragment} from '@/graphql-types'
import type {FormConfig} from '@/types/consents'

export const ReferralHeroSlice = ({slice}: {slice: ReferralHeroSliceFragment}) => {
    const {
        image,
        title,
        lead,
        inverse,
        headline,
        background,
        alternative_lead: alternativeLead,
        headline_background_color: headlineBackgroundColor,
        predefined_signup_banner: {
            document: {data: formContent},
        },
    } = slice.primary

    const color = isValidBackgroundColor(background) ? background : 'default'
    const referrerName = Cookies.get(paramCookiesMap.latestReferrerName)

    const leadText = replacePlaceholders(lead, {
        '{{referrer_name}}': decodeBase64(referrerName || ''),
    }).join('')

    return (
        <BackgroundWrapper
            $color={color}
            as={motion.div}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
        >
            <Section padding="small">
                <FeatureBlock
                    image={image}
                    preTitle={headline}
                    preTitleBackgroundColor={
                        isValidTagColor(headlineBackgroundColor)
                            ? headlineBackgroundColor
                            : undefined
                    }
                    title={title}
                    body={referrerName ? leadText : alternativeLead}
                    mirrored={!!inverse}
                    titleVariant="heading-large"
                    bodyProps={{$variant: 'paragraph-large'}}
                    headingLevel="h1"
                >
                    {formContent && (
                        <SimpleSignupForm
                            ariaLabel={formContent.aria_label}
                            placeholder={formContent.email_input_placeholder}
                            submitText={formContent.submit_text}
                            errorText={formContent.email_input_error_text}
                            signupOptions={{
                                form: formContent.form_name,
                                isPartner: formContent.partner_signup_redirect,
                                redirectPath:
                                    formContent.redirect_link?.uid &&
                                    `/${formContent.redirect_link.uid}`,
                            }}
                            formConfig={
                                getEnvironmentFormConfig({
                                    production: formContent.form_production,
                                    staging: formContent.form_staging,
                                }) as FormConfig
                            }
                            withBorder
                        />
                    )}
                </FeatureBlock>
            </Section>
        </BackgroundWrapper>
    )
}

export const referralHeroSliceFragment = graphql`
    fragment ReferralHeroSlice on PrismicSlicePageDataBodyReferralHero {
        __typename
        primary {
            headline
            headline_background_color
            title
            lead
            alternative_lead
            inverse
            background
            predefined_signup_banner {
                document {
                    ...SignupBanner
                }
            }
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
        }
    }
`
